import React from "react";
import { useTranslation } from "react-i18next";
import Button from "../TailwindComponents/Button";
import Badge from "../TailwindComponents/Badge";
import {
  PiCalendarBlankLight,
  PiTimerLight,
  PiUsersLight,
  PiArrowUpLight,
  PiShieldCheckLight,
  PiRocketLaunchLight,
  PiCrownLight,
  PiPackageLight,
} from "react-icons/pi";
import { IconType } from "react-icons";
import { OrgLicenseAccess, LicenseStatus } from "../../../store/actions/PlanActions";

const TIER_ICONS: Record<string, { icon: IconType; color: string }> = {
  starter: { icon: PiShieldCheckLight, color: "text-fg-strong" },
  professional: { icon: PiRocketLaunchLight, color: "text-fg-strong" },
  enterprise: { icon: PiCrownLight, color: "text-fg-strong" },
};

const getTierIcon = (planName: string | null) => {
  if (!planName) return { icon: PiPackageLight, color: "text-fg-strong" };
  const key = planName.toLowerCase();
  for (const [tier, cfg] of Object.entries(TIER_ICONS)) {
    if (key.includes(tier)) return cfg;
  }
  return { icon: PiPackageLight, color: "text-fg-strong" };
};

interface CurrentPlanBannerProps {
  access: OrgLicenseAccess | null;
  status: LicenseStatus | null;
  isOrgAdmin?: boolean;
  onChangePlan: () => void;
  onAddSeats?: () => void;
  onManageSeats?: () => void;
}

const CurrentPlanBanner: React.FC<CurrentPlanBannerProps> = ({
  access,
  status,
  isOrgAdmin = true,
  onChangePlan,
  onAddSeats,
  onManageSeats,
}) => {
  const { t } = useTranslation();

  if (!status) return null;

  const licenseInfo = access?.license_info;
  const planName = licenseInfo?.plan_display_name || status.display_name || status.base_plan || null;
  // Non-admin users should only see addons assigned to them
  const activeAddons = (!isOrgAdmin && licenseInfo?.user_addons)
    ? licenseInfo.user_addons
    : (licenseInfo?.active_addons || status.active_addons || []);
  const tierCfg = getTierIcon(planName || status.base_plan || null);
  const TierIcon = tierCfg.icon;

  const formatDate = (dateString: string | null) => {
    if (!dateString) return t("No expiry");
    return new Date(dateString).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="rounded-md border border-hairline-soft bg-surface-sunken/70 overflow-hidden flex flex-col">
      {/* Header */}
      <div className="p-5 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <TierIcon className={`h-5 w-5 ${tierCfg.color} flex-shrink-0`} />
            <h4 className="font-medium text-fg-strong text-sm">
              {planName || t("No Active Plan")}
            </h4>
            {status.has_active_license && (
              <Badge color="green">
                {t("Active")}
              </Badge>
            )}
            {status.is_trial && (
              <Badge color="yellow">
                {t("Trial")}
              </Badge>
            )}
            {!status.has_active_license && (
              <Badge color="red">
                {t("Inactive")}
              </Badge>
            )}
          </div>
          <p className="text-xs text-fg-muted mt-1.5">
            {status.has_active_license
              ? t("Your current subscription plan")
              : t("No active subscription")}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {status.has_active_license && onManageSeats && (
            <Button
              variant="outline"
              size="small"
              onClick={onManageSeats}
              icon={<PiUsersLight />}
            >
              {t("Manage Seats")}
            </Button>
          )}
          {status.has_active_license && !status.is_trial && onAddSeats && (
            <Button
              variant="outline"
              size="small"
              onClick={onAddSeats}
              icon={<PiUsersLight />}
            >
              {t("Add Seats")}
            </Button>
          )}
          <Button
            variant="primary"
            size="small"
            onClick={onChangePlan}
            icon={<PiArrowUpLight />}
          >
            {status.has_active_license ? t("Change Plan") : t("Get Started")}
          </Button>
        </div>
      </div>

      {/* Details - only if active */}
      {status.has_active_license && (
        <div className="border-t border-hairline-soft px-5 py-4 grid grid-cols-2 md:grid-cols-4 gap-6">
          {/* Trial countdown */}
          {status.is_trial && status.trial_days_remaining !== null && (
            <div className="flex items-center gap-3">
              <PiTimerLight className="h-4 w-4 text-fg-strong flex-shrink-0" />
              <div>
                <p className="text-xs text-fg-muted mb-1">{t("Trial Ends In")}</p>
                <p className="text-sm font-medium text-fg-strong">
                  {status.trial_days_remaining} {t("days")}
                </p>
              </div>
            </div>
          )}

          {/* Renewal / Expiry */}
          {(status.expires_at || licenseInfo?.expires_at) && (
            <div className="flex items-center gap-3">
              <PiCalendarBlankLight className="h-4 w-4 text-fg-strong flex-shrink-0" />
              <div>
                <p className="text-xs text-fg-muted mb-1">
                  {status.is_trial ? t("Trial Ends") : t("Renews On")}
                </p>
                <p className="text-sm font-medium text-fg-strong">
                  {formatDate(status.expires_at || licenseInfo?.expires_at || null)}
                </p>
              </div>
            </div>
          )}

          {/* Seats */}
          {(status.seat_count > 0 || (licenseInfo?.seat_count ?? 0) > 0) && (
            <div className="flex items-center gap-3">
              <PiUsersLight className="h-4 w-4 text-fg-strong flex-shrink-0" />
              <div>
                <p className="text-xs text-fg-muted mb-1">{t("Seats")}</p>
                <p className="text-sm font-medium text-fg-strong">
                  {licenseInfo
                    ? `${licenseInfo.seats_used} / ${licenseInfo.seat_count}`
                    : `${status.user_count} / ${status.seat_count}`}
                </p>
              </div>
            </div>
          )}

          {/* Active add-ons */}
          {activeAddons.length > 0 && (
            <div className="col-span-2">
              <p className="text-xs text-fg-muted mb-1">{t("Add-ons")}</p>
              <div className="flex flex-wrap gap-1.5">
                {activeAddons.map((name: string, idx: number) => (
                  <Badge key={idx} color="gray">
                    {name}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CurrentPlanBanner;
